import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Box,
  TextField,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import { Handlers } from "../api/handlers";
import StyledDialogTitle from "../../../common/components/dialog/dialog-title";
import useUserStore from "../api/store";
import { useConfirmPassword } from "../api/hooks";
import { enqueueSnackbar } from "notistack";
import usePasswordStore from "./api/store";
import { commonButtonProps } from "../../../common/components/buttons/sxprops";

const PasswordConfirmDialog: React.FC = () => {
  const { control, handleSubmit, reset } = useForm<{ password: string }>();
  const { mutate: confirmPassword } = useConfirmPassword();
  const { setOldPassword } = useUserStore();
  const {
    setIsEditingEmail,
    setIsEditingMobileNumber,
    setIsEditingPassword,
    currentEditContext,
    setCurrentEditContext,
  } = useUserStore();

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const onFormSubmit = ({ password }: { password: string }) => {
    confirmPassword(
      { oldPassword: password },
      {
        onSuccess: () => {
          enqueueSnackbar("Password confirmed.", { variant: "success" });
          switch (currentEditContext) {
            case "email":
              setIsEditingEmail(true);

              break;
            case "mobileNumber":
              setIsEditingMobileNumber(true);
              break;
            case "password":
              setIsEditingPassword(true);
              setOldPassword(password);

              break;
          }

          setCurrentEditContext("");
          setAddModal(false);
          reset();
          resetData();
        },
        onError: (err) => {
          console.error("Password validation failed", err);
        },
      },
    );
  };

  const handlers = new Handlers();

  const { isAddModalOpen, setAddModal } = useUserStore();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    showPassword,
    toggleShowPassword,
    handlePasswordChange,
    clearPassword,
    getDisplayValue,
    resetData,
  } = usePasswordStore();

  const handleClickShowPassword = () => {
    toggleShowPassword(!showPassword);
  };

  useEffect(() => {
    if (!isAddModalOpen) {
      reset();
      toggleShowPassword(false);
    }
  }, [isAddModalOpen, reset, toggleShowPassword]);
  return (
    <Dialog
      open={isAddModalOpen}
      onClose={() => {
        handlers.handleClosePasswordDialog();
        resetData();
      }}
      slotProps={{
        paper: {
          id: "security-password-confirm-dialog",
          "data-testid": "security-password-confirm-dialog",
          sx: { borderRadius: "24px" },
        } as any,
      }}
      keepMounted
      aria-describedby="confirm-password-dialog"
      maxWidth={"tablet"}
      fullWidth
    >
      <StyledDialogTitle
        title="Enter password to continue"
        testIdPrefix="security-password-confirm-dialog"
        actionType="close"
        handleClose={() => {
          handlers.handleModalClose();
          resetData();
        }}
      />

      <DialogContent sx={{ minWidth: "300px" }}>
        <Box
          sx={{
            width: "100%",
            padding: "10px",
            display: "flex",
            flexDirection: "column",

            gap: 1,
          }}
        >
          <Typography fontSize={16} color="primary.main" sx={{ mb: 1 }}>
            We need to confirm first if it’s really you.
          </Typography>

          <form onSubmit={handleSubmit(onFormSubmit)} autoComplete="off">
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => {
                const handleChange = (
                  e: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  const newValue = e.target.value;
                  const cursorPosition = e.target.selectionStart;

                  // Update store and get the actual password
                  const actualPassword = handlePasswordChange(
                    newValue,
                    cursorPosition || 0,
                  );

                  // Update react-hook-form
                  field.onChange(actualPassword);
                };

                const handlePaste = (
                  e: React.ClipboardEvent<HTMLInputElement>,
                ) => {
                  e.preventDefault();
                  clearPassword();
                  field.onChange("");
                };

                return (
                  <TextField
                    ref={inputRef}
                    type="text"
                    id="password-input"
                    label="Enter Password"
                    margin="dense"
                    size="medium"
                    variant="outlined"
                    required
                    error={!!fieldState?.error}
                    helperText={fieldState?.error?.message}
                    fullWidth
                    value={getDisplayValue()}
                    autoComplete="off"
                    onPaste={handlePaste}
                    onChange={handleChange}
                    slotProps={{
                      input: {
                        autoComplete: "off",
                        inputProps: {
                          autoComplete: "off",
                          "data-lpignore": "true",
                          "data-1p-ignore": "true",
                          "data-bwignore": "true",
                          "data-form-type": "other",
                          "data-testid": "password-input",
                          spellCheck: "false",
                        },
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{ alignItems: "center" }}
                          >
                            <IconButton
                              id="security-password-confirm-toggle-visibility-button"
                              data-testid="security-password-confirm-toggle-visibility-button"
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              sx={{ color: "#f44336" }}
                              size="small"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                );
              }}
            />
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button
          id="security-password-confirm-cancel-button"
          data-testid="security-password-confirm-cancel-button"
          onClick={() => {
            handlers.handleModalClose();
            resetData();
          }}
          variant="text"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          id="security-password-confirm-continue-button"
          data-testid="security-password-confirm-continue-button"
          variant="contained"
          onClick={handleSubmit(onFormSubmit)}
          sx={commonButtonProps}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordConfirmDialog;
